import type { APIError } from './setAPIErrors'

const GLOBAL_API_ERROR_KEYS = ['non_field_errors', 'common', 'detail'] as const

export const extractGlobalApiErrors = (data: unknown): string[] => {
  if (!data || typeof data !== 'object') {
    return []
  }

  const payload = data as Record<string, unknown>
  const extractedErrors: string[] = []

  for (const key of GLOBAL_API_ERROR_KEYS) {
    const value = payload[key]
    if (Array.isArray(value)) {
      extractedErrors.push(...value.filter((entry): entry is string => typeof entry === 'string'))
    } else if (typeof value === 'string') {
      extractedErrors.push(value)
    }
  }

  return extractedErrors
}

export const stripGlobalApiErrorKeys = (data: Record<string, unknown>): APIError => {
  const fieldErrors = { ...data } as APIError
  for (const key of GLOBAL_API_ERROR_KEYS) {
    delete fieldErrors[key]
  }
  return fieldErrors
}

const RESERVED_API_ERROR_KEYS = new Set([
  ...GLOBAL_API_ERROR_KEYS,
  'error_type',
  'error_code',
  'message',
  'field_errors',
  'hint',
  'status_code',
  'details'
])

const toFieldErrorMap = (value: unknown): APIError | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  const fieldErrors: APIError = {}
  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    if (RESERVED_API_ERROR_KEYS.has(key)) {
      continue
    }
    fieldErrors[key] = entry as APIError[string]
  }

  return Object.keys(fieldErrors).length > 0 ? fieldErrors : null
}

export const getApiFieldErrors = (data: unknown): APIError | null => {
  if (!data || typeof data !== 'object') {
    return null
  }

  const payload = data as Record<string, unknown>
  return (
    toFieldErrorMap(payload.field_errors) ||
    toFieldErrorMap(payload.details) ||
    toFieldErrorMap(stripGlobalApiErrorKeys(payload))
  )
}

export const firstApiErrorMessage = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.trim()) {
    return value
  }
  if (Array.isArray(value) && value.length > 0) {
    return firstApiErrorMessage(value[0])
  }
  if (value && typeof value === 'object' && 'message' in value) {
    return firstApiErrorMessage((value as { message: unknown }).message)
  }
  return undefined
}
